/** Čisté výpočty obrazovky statistik. Bez Reactu, ať jdou spustit testem. */

export type PeriodId =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'lastWeek'
  | '7d'
  | '30d'
  | 'this'
  | 'last'
  | 'custom';

export type SyncAgo =
  | { kind: 'never' }
  | { kind: 'now' }
  | { kind: 'min'; count: number }
  | { kind: 'hour'; count: number }
  | { kind: 'day'; count: number };

export function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Pondělí kalendářního týdne (týden začíná v pondělí). */
function mondayOf(date: Date): Date {
  const day = date.getDay();
  return addDays(date, day === 0 ? -6 : 1 - day);
}

export function periodRange(
  id: PeriodId,
  now: Date,
  custom?: { from: string; to: string },
): { from: string; to: string } {
  const today = ymd(now);
  switch (id) {
    case 'custom': {
      let from = custom?.from || today;
      let to = custom?.to || today;
      if (from > to) [from, to] = [to, from];
      return { from, to };
    }
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const day = ymd(addDays(now, -1));
      return { from: day, to: day };
    }
    case 'week':
      return { from: ymd(mondayOf(now)), to: today };
    case 'lastWeek': {
      const start = addDays(mondayOf(now), -7);
      return { from: ymd(start), to: ymd(addDays(start, 6)) };
    }
    case 'this':
      return { from: `${today.slice(0, 8)}01`, to: today };
    case 'last': {
      const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastPrev = new Date(firstThis.getTime() - 24 * 60 * 60 * 1000);
      const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
      return { from: ymd(firstPrev), to: ymd(lastPrev) };
    }
    case '7d':
    case '30d': {
      const days = id === '7d' ? 7 : 30;
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
      return { from: ymd(start), to: today };
    }
  }
}

export function calendarDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return ymd(date);
}

/** Varování, když poslední tisk je starší než 24 h a dnes je pracovní den v Praze. */
export function printEventStale(lastEventAt: string | null, now: Date): boolean {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Prague',
    weekday: 'short',
  }).format(now);
  if (weekday === 'Sat' || weekday === 'Sun') return false;
  if (!lastEventAt) return true;
  const at = Date.parse(lastEventAt);
  if (Number.isNaN(at)) return true;
  return now.getTime() - at > 24 * 60 * 60 * 1000;
}

export function syncAgo(iso: string | null | undefined, now: Date): SyncAgo {
  if (!iso) return { kind: 'never' };
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return { kind: 'never' };
  const minutes = Math.floor(Math.max(0, now.getTime() - at) / 60_000);
  if (minutes < 1) return { kind: 'now' };
  if (minutes < 60) return { kind: 'min', count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return { kind: 'hour', count: hours };
  return { kind: 'day', count: Math.floor(hours / 24) };
}

export function syncAdded(
  lastSyncAt: string | null,
  servers: { last_run_at: string | null; last_run_added: number }[],
): number | null {
  if (!lastSyncAt) return null;
  const syncMs = Date.parse(lastSyncAt);
  const same = servers.filter((server) => {
    if (!server.last_run_at) return false;
    if (server.last_run_at === lastSyncAt) return true;
    const runMs = Date.parse(server.last_run_at);
    return !Number.isNaN(syncMs) && !Number.isNaN(runMs) && Math.abs(syncMs - runMs) < 2000;
  });
  if (same.length === 0) return null;
  return same.reduce((sum, server) => sum + (server.last_run_added || 0), 0);
}

export function sharePct(part: number, total: number): number {
  if (total <= 0 || part <= 0) return 0;
  return Math.round((part / total) * 100);
}

// ── Export CSV ──────────────────────────────────────────────────────────────

/** Strop řádků exportu seznamu úloh (stránkuje se po 200). */
export const CSV_JOBS_CAP = 50_000;

/**
 * CSV pro český Excel: UTF-8 s BOM, oddělovač `;`, konce řádků CRLF. Buňka
 * s oddělovačem, uvozovkou nebo koncem řádku jde do uvozovek (uvozovky
 * zdvojené). Hodnota začínající = + - @ dostane apostrof (vzorec v Excelu).
 */
export function csvDocument(headers: string[], rows: string[][], separator = ';'): string {
  const cell = (raw: string) => {
    const value = /^[=+\-@\t\r]/.test(raw) && !/^-?\d/.test(raw) ? `'${raw}` : raw;
    return value.includes(separator) || /["\r\n]/.test(value)
      ? `"${value.replace(/"/g, '""')}"`
      : value;
  };
  const lines = [headers, ...rows].map((row) => row.map(cell).join(separator));
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

/** Desetinná čárka podle jazyka (en tečka), bez oddělovače tisíců — Excel to přečte jako číslo. */
export function csvNumber(value: number, language: string, decimals = 0): string {
  const n = Number.isFinite(value) ? value : 0;
  const text = decimals > 0 ? n.toFixed(decimals) : String(Math.round(n));
  return language.startsWith('en') ? text : text.replace('.', ',');
}

/** Datum a čas úlohy pro tabulku: místní čas „2026-10-05 09:07". */
export function csvDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${ymd(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fileSlug(value: string, fallback: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || fallback;
}

/** tisky-<zákazník>[-<uživatel>]-<od>_<do>[-jobs].csv */
export function csvFilename(
  customer: string,
  user: string | null,
  from: string,
  to: string,
  jobs: boolean,
): string {
  const who = user ? `-${fileSlug(user, 'uzivatel')}` : '';
  return `tisky-${fileSlug(customer, 'zakaznik')}${who}-${from}_${to}${jobs ? '-jobs' : ''}.csv`;
}

/** 2026-10-27 → 27. 10. 2026. Neznámý tvar nechá tak, jak přišel. */
export function formatDay(ymd: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!match) return ymd;
  return `${Number(match[3])}. ${Number(match[2])}. ${match[1]}`;
}

/** Částka v měně zákazníka (Kč) podle jazyka UI; vždy 2 desetinná místa. */
export function formatMoney(value: number, language: string, currency = 'CZK'): string {
  const loc = language.startsWith('en') ? 'en-GB' : language.startsWith('it') ? 'it-IT' : 'cs-CZ';
  return new Intl.NumberFormat(loc, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

/**
 * Cena z formuláře: prázdné = nezměněno (null), jinak nezáporné číslo s nejvýš
 * dvěma desetinnými místy, desetinná čárka i tečka. Neplatné = 'invalid'.
 */
export function parsePriceInput(text: string, max = 100_000): number | null | 'invalid' {
  const value = text.trim().replace(',', '.');
  if (!value) return null;
  if (!/^\d{1,6}(\.\d{1,2})?$/.test(value)) return 'invalid';
  const n = Number(value);
  return n > max ? 'invalid' : n;
}

/** Klíč osobní volby „Zobrazit ceny“ v ui.prefs (`{show}`; čte ji printserver.print_stats). */
export const SHOW_COST_PREF = 'printserver.show_cost';

/** Klíč osobní volby výchozího zákazníka v ui.prefs (čte ji printserver.print_customers). */
export const DEFAULT_CUSTOMER_PREF = 'printserver.default_customer';

export interface CustomerOption {
  id: string;
  label: string;
}

/**
 * Zákazníci do výběru: seznam z print_customers + id z nápovědy `customer_required`,
 * která v seznamu chybí (bez duplicit, pořadí ze serveru zůstává).
 */
export function mergeCustomers<T extends CustomerOption>(
  list: T[],
  hints: string[],
): CustomerOption[] {
  const known = new Set(list.map((item) => item.id));
  const extra = [...new Set(hints)].filter((id) => id && !known.has(id)).map((id) => ({
    id,
    label: id,
  }));
  return [...list, ...extra];
}

/**
 * Vybraný zákazník: ruční volba, dokud je v seznamu; jinak výchozí ze serveru
 * (osobní, modul, nebo jediný zákazník). Jediný zákazník se tak vybere sám, bez kliknutí.
 */
export function chooseCustomer(
  picked: string,
  list: CustomerOption[],
  defaultCustomer: string | null | undefined,
): string {
  if (picked && list.some((item) => item.id === picked)) return picked;
  if (defaultCustomer && list.some((item) => item.id === defaultCustomer)) return defaultCustomer;
  return '';
}

/** Výběr zákazníka jen v modulu a jen když je z čeho vybírat. Aplikace Tisky ho nemá nikdy. */
export function showCustomerPicker(mode: 'module' | 'app', count: number): boolean {
  return mode === 'module' && count > 1;
}

const PRINT_ERROR_KEYS: Record<string, string> = {
  customer_required: 'printserver.stats.errCustomer',
  from_to_required: 'printserver.stats.errRange',
  group_by_required: 'printserver.stats.errGroups',
  pdf_render_failed: 'printserver.stats.errPdf',
  pdf_busy: 'printserver.stats.errPdfBusy',
  invalid_price: 'printserver.pricing.errPrice',
  invalid_a3_multiplier: 'printserver.pricing.errA3',
  invalid_label: 'printserver.pricing.errLabel',
  printer_unknown: 'printserver.pricing.errPrinter',
  nothing_to_change: 'printserver.pricing.errNothing',
  forbidden: 'printserver.stats.errForbidden',
  no_customer_for_tenant: 'printserver.stats.errNoCustomer',
  ambiguous_customer_for_app: 'printserver.stats.errAmbiguous',
};

/** Kód z print_* toolu na klíč překladu. Neznámý kód je null, ať se v UI neukáže surový identifikátor. */
export function printErrorKey(code: string): string | null {
  return PRINT_ERROR_KEYS[code] ?? null;
}

/** Výchozí řazení seznamu úloh (stejné jako výchozí print_jobs): nejnovější první. */
export const DEFAULT_JOB_SORT: { key: string; dir: 'asc' | 'desc' } = {
  key: 'printed_at',
  dir: 'desc',
};

/** Sloupce, podle kterých print_jobs řadí; klíč sloupce tabulky = hodnota `sort`. */
export const JOB_SORT_KEYS = [
  'printed_at',
  'user',
  'printer',
  'pages',
  'total_pages',
  'paper_size',
  'color_mode',
  'cost',
] as const;

/** Stav řazení tabulky úloh → argumenty `sort` / `order` pro printserver.print_jobs. */
export function jobSortArgs(sort: { key: string; dir: 'asc' | 'desc' }): {
  sort: string;
  order: 'asc' | 'desc';
} {
  if (!(JOB_SORT_KEYS as readonly string[]).includes(sort.key)) {
    return { sort: DEFAULT_JOB_SORT.key, order: DEFAULT_JOB_SORT.dir };
  }
  return { sort: sort.key, order: sort.dir === 'asc' ? 'asc' : 'desc' };
}
